import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { FaEnvelope, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { authAPI, translateBackendMessage } from "../../api/apiSystem";
import "./LoginForm.css";

const FormField = ({
  controlId,
  label,
  type,
  placeholder,
  value,
  onChange,
  icon: Icon,
}) => (
  <Form.Group className="mb-3" controlId={controlId}>
    <Form.Label>{label}</Form.Label>
    <div className="position-relative">
      <Icon className="form-icon" />
      <Form.Control
        type={type}
        placeholder={placeholder}
        className="ps-5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
    </div>
  </Form.Group>
);

export default function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [isVehicleLogin, setIsVehicleLogin] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: "OK",
      confirmButtonColor: "#fd7e14",
      background: "#f8f9fa",
      color: "#333",
    });
  };

  const showSuccessAlert = (userType) => {
    const messages = {
      client: t("loginForm.alerts.success.client"),
      driver: t("loginForm.alerts.success.driver"),
      default: t("loginForm.alerts.success.default"),
    };

    Swal.fire({
      icon: "success",
      title: t("loginForm.alerts.success.title"),
      text: messages[userType] || messages.default,
      timer: 2000,
      showConfirmButton: false,
      background: "#f8f9fa",
      color: "#333",
    });
  };

  const determineUserType = (roleName) => {
    const role = roleName?.toLowerCase();
    if (role === "user") return "client";
    if (role === "driver") return "driver";
    if (role === "driver company") return "driver_company";
    return null;
  };

  const getNavigationPath = (userType) => {
    const paths = {
      client: "/",
      driver: "/driver/driver-details",
      driver_company: "/driver-company/active-orders",
      vehicle: "/vehicle/active-orders",
    };
    return paths[userType] || "/";
  };

  const handleLoginSuccess = (response) => {
    const { access_token, token, driver, user, vehicle } = response.data.data;
    const loggedUser = driver || user || vehicle;

    localStorage.setItem("token", access_token || token);
    localStorage.setItem("user", JSON.stringify(loggedUser));

    const userType = determineUserType(loggedUser?.role_name) || (vehicle ? "vehicle" : null);

    if (userType) {
      localStorage.setItem("userType", userType);
      window.dispatchEvent(new Event("userTypeChanged"));

      showSuccessAlert(userType);
      setTimeout(() => {
        navigate(getNavigationPath(userType));
      }, 2000);
    } else {
      navigate("/");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      showAlert(
        "error",
        t("loginForm.alerts.error.title"),
        t("loginForm.error.requiredFields")
      );
      return;
    }

    setLoading(true);

    try {
      const response = isVehicleLogin 
        ? await authAPI.vehicleLogin(formData)
        : await authAPI.login(formData);

      if (response.success && response.data?.data?.access_token) {
        handleLoginSuccess(response);
      } else {
        const errorMessage =
          response.message || t("loginForm.error.invalidCredentials");
        showAlert("error", t("loginForm.alerts.error.title"), errorMessage);
      }
    } catch (error) {
      const errorMessage =
        translateBackendMessage(
          error?.response?.data || error,
          t("loginForm.error.networkError"),
        );
      showAlert("error", t("loginForm.alerts.error.title"), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const navigateToConfirmEmail = () => {
    navigate("/confirm-email");
  };

  const navigateToRegister = (e) => {
    e.preventDefault();
    navigate("/register");
  };

  return (
    <div className="login-section">
      <Container>
        <Row className="justify-content-center align-items-center h-100">
          <Col md={6} lg={5}>
            <Card className="shadow-lg p-4 border-0 login-card">
              <Card.Body>
                <h2 className="text-center mb-4 text-dark fw-bold">
                  {t("loginForm.title")}
                </h2>
                <p className="text-center text-muted mb-4">
                  {t("loginForm.description")}
                </p>

                <Form onSubmit={handleLogin}>
                  <FormField
                    controlId="formEmail"
                    label={t("loginForm.emailLabel")}
                    type="email"
                    placeholder={t("loginForm.emailPlaceholder")}
                    value={formData.email}
                    onChange={(value) => handleInputChange("email", value)}
                    icon={FaEnvelope}
                  />

                  <Form.Group className="mb-3" controlId="formPassword">
                    <Form.Label>{t("loginForm.passwordLabel")}</Form.Label>

                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder={t("loginForm.passwordPlaceholder")}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                      />

                      <span
                        className="password-toggle-icon"
                        onClick={() => setShowPassword(prev => !prev)}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    {/* <Form.Check
                      type="checkbox"
                      label={t("loginForm.rememberMe")}
                    /> */}
                    <a
                      href="/forget-password"
                      className="text-orange text-decoration-none"
                    >
                      {t("loginForm.forgotPassword")}
                    </a>
                  </div>

                  <Button
                    variant="warning"
                    type="submit"
                    className="w-100 bg-orange-500 text-white mb-3"
                    disabled={loading}
                  >
                    {loading
                      ? t("loginForm.submitButtonLoading")
                      : t("navbar.login")}
                  </Button>
                </Form>

                <Button
                  variant="outline-secondary"
                  className="w-100 mb-3 sub-login"
                  onClick={() => navigate("/vehicle-login")}
                >
                  {t("loginForm.subDriverLogin") || "تسجيل سائق فرعي"}
                </Button>

                <Button
                  className="w-100 client-btn"
                  onClick={navigateToConfirmEmail}
                >
                  {t("loginForm.confirmEmailButton")}
                </Button>

                <hr className="my-4" />

                <p className="text-center">
                  {t("loginForm.noAccount")}{" "}
                  <a
                    href="#"
                    onClick={navigateToRegister}
                    className="text-orange text-decoration-none"
                  >
                    {t("loginForm.registerLink")}
                  </a>
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
